import React, { useCallback, useEffect, useState } from 'react';
import AttachmentsCard from './AttachmentsCard';
import PharmacyCard from './PharmacyCard';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import { getMedicationDisplayName } from './medicationUtils';
import MedicationFormDialog, {
  type MedicationFormMode,
} from './MedicationFormDialog';
import MedicationsCard from './MedicationsCard';
import { formatDateToISO } from '../../utils/dateUtils';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { useToast } from '../../hooks/useToastContext';
import {
  deletePatientMedication,
  getImportedMedicationLines,
  getPatientMedications,
  updatePatientMedication,
} from '../../services/api';
import type {
  ImportedMedicationProfileResponse,
  OrderResponse,
  PatientMedicationListItem,
  PatientMedicationResponse,
} from '../../services/api';
import PatientDataImporterTool from '../imported/PatientDataImporterTool';
import { useCanReconcileImports } from '../imported/useCanReconcileImports';
import {
  convertedLines,
  outstandingLines,
} from '../imported/importedChartLines';
import { IMPORTED_FETCH_TRANSIENT_TOAST } from '../imported/importedChartLineCopy';
import type { TaskStatus } from '../../services/api/types.gen';
import { EDITABLE_STATUSES } from '../../constants/statuses';
import { useClosedEpisodeLock } from '../../utils/closedEpisodeLock';
import MarDialogs from './mar/MarDialogs';
import { useMarRecords } from './mar/useMarRecords';
import {
  contradictionAnchor,
  contradictionLoadingAnchor,
} from '../oasis/contradictions/contradictionNavigation';

interface PatientMedicationsProps {
  patientId: string;
  /** Episode open in the chart; scopes medication order creation to it. */
  episodeId?: string;
  /** When a visit is selected on the Medications tab, MAR affordances are enabled. */
  visitId?: string;
  /** Status of the selected visit; administrations are only editable on open visits. */
  visitStatus?: TaskStatus;
  /** True when the visit's period is closed (EMR-1859). */
  episodeClosed?: boolean;
  /** True while the chart is an imported one still charted in the External
   *  EMR (Take Over v2). Every write on this tab steps aside. */
  chartLocked?: boolean;
  /** Visit selector rendered in the card's action row (state lives in the page). */
  visitSelector?: React.ReactNode;
  onMedicationOrderCreated: (order: OrderResponse) => void;
  onDocumentCreated?: () => void;
  /**
   * Called after the medication set changes (add/edit, delete, or discontinue).
   * The parent uses this to refresh its cached medication count so the tab badge
   * stays correct without waiting for a tab switch (ROG-3195). A MAR change is
   * deliberately excluded — it flips `has_administration_records`, not the
   * non-discontinued count the badge shows.
   *
   * Consumers rely on this firing after *every* successful medication mutation,
   * not only when the badge count changes: `OASISForm` uses it to re-run Live
   * Scrubber validation, whose server-side contradiction snapshot reads
   * medication rows from the database (EMR-974). Do not narrow it to count-only
   * changes.
   */
  onMedicationsChanged?: () => void;
}

const PatientMedications: React.FC<PatientMedicationsProps> = ({
  patientId,
  episodeId,
  visitId,
  visitStatus,
  episodeClosed,
  chartLocked = false,
  visitSelector,
  onMedicationOrderCreated,
  onDocumentCreated,
  onMedicationsChanged,
}) => {
  const [medications, setMedications] = useState<PatientMedicationListItem[]>(
    []
  );
  // MAR can only be recorded on a selected visit that is still editable.
  const { locked: closedEpisodeLocked } = useClosedEpisodeLock(episodeClosed);
  const visitEditable =
    Boolean(visitId) &&
    !!visitStatus &&
    EDITABLE_STATUSES.has(visitStatus) &&
    !closedEpisodeLocked &&
    !chartLocked;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { error: showError } = useToast();

  // Raw Kinnser lines and the reconciliation screen that rules on them.
  const [importedProfile, setImportedProfile] =
    useState<ImportedMedicationProfileResponse | null>(null);
  const [importedFetchFailed, setImportedFetchFailed] = useState(false);
  const [importerOpen, setImporterOpen] = useState(false);
  const canReconcileImports = useCanReconcileImports();
  // Only lines nobody has ruled on: a reconciled line is already on the chart
  // as the medication it became.
  const outstandingImportedMedicationLines = outstandingLines(
    importedProfile?.lines
  );
  // Coded by the import on a certain match (EMR-2408); revertible in the tool.
  const convertedImportedMedicationLines = convertedLines(
    importedProfile?.lines
  );

  // Add/edit form dialog control (the form owns its own field state).
  const [formOpen, setFormOpen] = useState(false);
  const [formEditing, setFormEditing] =
    useState<PatientMedicationResponse | null>(null);
  const [formMode, setFormMode] = useState<MedicationFormMode>('standard');

  // Delete confirmation
  const [deletingMedication, setDeletingMedication] =
    useState<PatientMedicationResponse | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchMedications = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getPatientMedications({
        path: { patient_id: patientId },
        query: { include_discontinued: true },
      });
      setMedications(response?.data ?? []);
    } catch (err) {
      console.error('Failed to fetch medications:', err);
      setError('Failed to load medications');
    } finally {
      setIsLoading(false);
    }
  }, [patientId]);

  /**
   * The raw Kinnser lines, fetched from their own endpoint and kept in their
   * own state.
   *
   * A failure must not blank the coded list — that one is clinically
   * load-bearing — but it must not be silent either (2026-08-28 ruling): a
   * quiet failure hides the interaction-screening notice, so a patient with
   * unreconciled lines reads identically to one with none. An error strip
   * takes the section's place; a connection-class failure also toasts,
   * because there a retry is likely to work.
   */
  const fetchImportedLines = useCallback(async () => {
    const response = await getImportedMedicationLines({
      path: { patient_id: patientId },
    });
    if (response.error || !response.data) {
      console.error(
        'Failed to fetch imported medication lines:',
        response.error
      );
      setImportedProfile(null);
      setImportedFetchFailed(true);
      const status = getHttpStatus(response) ?? getHttpStatus(response.error);
      if (status === undefined) {
        showError(IMPORTED_FETCH_TRANSIENT_TOAST);
      }
      return;
    }
    setImportedFetchFailed(false);
    setImportedProfile(response.data);
  }, [patientId, showError]);

  useEffect(() => {
    fetchMedications();
    void fetchImportedLines();
  }, [fetchMedications, fetchImportedLines]);

  const mar = useMarRecords({
    patientId,
    visitId,
    // A MAR change flips a medication's `has_administration_records` flag.
    onRecordsChanged: fetchMedications,
  });

  const openAddDialog = (mode: MedicationFormMode = 'standard') => {
    setFormEditing(null);
    setFormMode(mode);
    setFormOpen(true);
  };

  const openEditDialog = (med: PatientMedicationResponse) => {
    setFormEditing(med);
    setFormOpen(true);
  };

  const handleDeleteMedication = async () => {
    if (!deletingMedication) return;
    setIsDeleting(true);
    try {
      // The client is configured without `throwOnError`, so HTTP failures come
      // back as a populated `error` rather than a thrown exception — inspect it
      // explicitly and never remove the row optimistically on failure.
      const response = await deletePatientMedication({
        path: { patient_id: patientId, medication_id: deletingMedication.id },
      });
      if (response.error) {
        // 409: the medication has administration records and can only be
        // discontinued. This is an actionable conflict, so tell the user what
        // to do instead of a generic message.
        showError(
          getHttpStatus(response) === 409
            ? 'This medication has administration records and can’t be deleted. Discontinue it instead.'
            : 'Failed to delete medication. Please try again.'
        );
        return;
      }
      setMedications(prev => prev.filter(m => m.id !== deletingMedication.id));
      setDeletingMedication(null);
      onMedicationsChanged?.();
    } catch (err) {
      console.error('Failed to delete medication:', err);
      showError('Failed to delete medication. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleQuickDiscontinue = async (medicationId: string, date: Date) => {
    try {
      // The client is configured without `throwOnError`, so HTTP failures come
      // back as a populated `error` rather than a thrown exception — inspect it
      // explicitly so a failed discontinue isn't silently dropped.
      const response = await updatePatientMedication({
        path: { patient_id: patientId, medication_id: medicationId },
        body: { discontinue_date: formatDateToISO(date) },
      });
      if (response.error) {
        console.error('Failed to discontinue medication:', response.error);
        showError('Failed to discontinue medication. Please try again.');
        return;
      }
      if (response.data) {
        setMedications(prev =>
          prev.map(m => (m.id === medicationId ? response.data! : m))
        );
      }
      // Discontinuing drops the med from the badge's non-discontinued count.
      onMedicationsChanged?.();
    } catch (err) {
      console.error('Failed to discontinue medication:', err);
      showError('Failed to discontinue medication. Please try again.');
    }
  };

  return (
    <div
      {...contradictionAnchor('medication_list')}
      {...contradictionLoadingAnchor(isLoading)}
    >
      <PharmacyCard patientId={patientId} readOnly={chartLocked} />

      {importerOpen ? (
        // Replaces the list rather than floating over it: the clinician is
        // reading one chart and writing another, and the source text must not
        // sit behind the form.
        <PatientDataImporterTool
          patientId={patientId}
          domain='medication'
          lines={outstandingImportedMedicationLines}
          convertedLines={convertedImportedMedicationLines}
          codedRecordNames={Object.fromEntries(
            medications.map(medication => [
              medication.id,
              medication.medication?.full_name ??
                medication.off_market_description ??
                '',
            ])
          )}
          onClose={() => setImporterOpen(false)}
          onChanged={() => {
            void fetchMedications();
            void fetchImportedLines();
            // A carried-forward line becomes a coded medication, which moves
            // the tab badge and re-runs Live Scrubber validation.
            onMedicationsChanged?.();
          }}
        />
      ) : (
        <MedicationsCard
          medications={medications}
          isLoading={isLoading}
          error={error}
          patientId={patientId}
          episodeId={episodeId}
          onRetry={fetchMedications}
          onAddMedication={openAddDialog}
          onEditMedication={openEditDialog}
          onDeleteMedication={setDeletingMedication}
          onDiscontinueMedication={handleQuickDiscontinue}
          onMedicationOrderCreated={onMedicationOrderCreated}
          onDocumentCreated={onDocumentCreated}
          visitId={visitId}
          visitEditable={visitEditable}
          visitSelector={visitSelector}
          marByMedicationId={mar.marByMedicationId}
          onAddMar={mar.actions.onAddRecord}
          onEditMar={mar.actions.onEditRecord}
          onDeleteMar={mar.actions.onDeleteRecord}
          readOnly={chartLocked}
          importedProfile={importedProfile}
          importedFetchFailed={importedFetchFailed}
          onRetryImported={() => void fetchImportedLines()}
          onOpenImporter={
            canReconcileImports && !chartLocked
              ? () => setImporterOpen(true)
              : undefined
          }
        />
      )}

      <AttachmentsCard patientId={patientId} readOnly={chartLocked} />

      <MedicationFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        patientId={patientId}
        editingMedication={formEditing}
        initialMode={formMode}
        onSaved={() => {
          void fetchMedications();
          // Adding a med, or editing one to set/clear a discontinue date, moves
          // the badge count.
          onMedicationsChanged?.();
        }}
      />

      <ConfirmDeleteDialog
        open={deletingMedication !== null}
        onOpenChange={open => !open && setDeletingMedication(null)}
        title='Delete Medication'
        description={
          <>
            Are you sure you want to delete{' '}
            <strong>
              {deletingMedication &&
                getMedicationDisplayName(deletingMedication)}
            </strong>
            ? This action cannot be undone.
          </>
        }
        onConfirm={handleDeleteMedication}
        isDeleting={isDeleting}
      />

      {visitId && (
        <MarDialogs patientId={patientId} visitId={visitId} mar={mar} />
      )}
    </div>
  );
};

export default PatientMedications;
